import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { Check, Phone, Mail, Building2, MessageSquare, Loader2, AlertCircle, Calendar } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function LandingPackSala() {
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    telefono: '',
    empresa: '',
    mensaje: '',
    necesita_hardware: ''
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      // Build complete message with hardware info at the beginning
      let mensajeCompleto = `--- INFORMACIÓN DEL CLIENTE ---\n`;
      mensajeCompleto += `¿Necesita hardware (TPV, tablets, comanderos)?: ${formData.necesita_hardware}\n`;
      
      if (formData.mensaje && formData.mensaje.trim()) {
        mensajeCompleto += `\n--- MENSAJE ADICIONAL ---\n${formData.mensaje}`;
      }
      
      const response = await axios.post(
        `${BACKEND_URL}/api/leads/web`,
        {
          nombre: formData.nombre,
          email: formData.email,
          telefono: formData.telefono,
          empresa: formData.empresa,
          mensaje: mensajeCompleto,
          interes: 'Pack Sala - TPV + 5 Comanderos',
          origen: 'Landing Pack Sala'
        },
        {
          headers: {
            'Content-Type': 'application/json'
          }
        }
      );

      if (response.data.success) {
        setSubmitted(true);
        toast.success('¡Solicitud enviada! Nos pondremos en contacto contigo pronto.');
        setFormData({ nombre: '', email: '', telefono: '', empresa: '', mensaje: '', necesita_hardware: '' });
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al enviar el formulario. Inténtalo de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <header className="bg-[#212B35] text-white py-4 px-6 shadow-lg">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo.png" alt="Camarero10" className="h-10 w-auto" />
          </div>
          <div className="flex items-center gap-4">
            <a href="tel:+34910052324" className="flex items-center gap-2 text-sm hover:text-[#9C0F17] transition-colors">
              <Phone className="w-4 h-4" />
              <span className="hidden sm:inline">910 052 324</span>
            </a>
            <a 
              href="https://wa.me/34647645711?text=Estoy%20interesado%20en%20Camarero10%20-%20Pack%20Sala"
              target="_blank"
              rel="noopener noreferrer"
              className="bg-green-500 hover:bg-green-600 text-white px-3 py-2 rounded-lg text-sm font-semibold flex items-center gap-2 transition-colors"
            >
              <MessageSquare className="w-4 h-4" />
              <span className="hidden sm:inline">WhatsApp</span>
            </a>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 py-12 lg:py-20">
        <div className="grid lg:grid-cols-2 gap-12 items-start">
          {/* Left Column - Product Info */}
          <div className="space-y-8">
            {/* Hero Section */}
            <div>
              <div className="inline-block bg-green-600 text-white px-4 py-1 rounded-full text-sm font-semibold mb-4">
                Incluye 5 comanderos
              </div>
              <h1 className="text-4xl lg:text-5xl font-bold text-[#212B35] mb-4">
                Camarero10 Pack Sala
              </h1>
              <p className="text-xl text-gray-600 mb-6">
                Todo lo del Pack Barra + Licencia de 5 comanderos adicionales
              </p>
              
              {/* Pricing Options */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <Card className="border-2 border-[#9C0F17]">
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Pago Mensual</p>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-[#9C0F17]">49,90€</span>
                        <span className="text-sm text-gray-600">+ IVA</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">/mes</p>
                    </div>
                  </CardContent>
                </Card>
                
                <Card className="border-2 border-green-600 relative">
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-green-600 text-white px-3 py-1 rounded-full text-xs font-bold">
                    AHORRA 17%
                  </div>
                  <CardContent className="pt-6">
                    <div className="text-center">
                      <p className="text-sm text-gray-600 mb-2">Pago Anual</p>
                      <div className="flex items-baseline justify-center gap-1">
                        <span className="text-4xl font-bold text-green-600">490€</span>
                        <span className="text-sm text-gray-600">+ IVA</span>
                      </div>
                      <p className="text-xs text-gray-500 mt-1">al año (equivale a 40,83€/mes + IVA)</p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Important Notice */}
              <Card className="bg-yellow-50 border-2 border-yellow-400">
                <CardContent className="pt-4 pb-4">
                  <div className="flex gap-3">
                    <AlertCircle className="w-6 h-6 text-yellow-600 flex-shrink-0" />
                    <div>
                      <p className="font-bold text-yellow-900 mb-2">⚠️ IMPORTANTE: Solo Licencias</p>
                      <p className="text-sm text-yellow-800">
                        <strong>NO incluye equipos</strong> (TPVs, tablets, impresoras, comanderos, routers). 
                        Puedes reutilizar tus equipos actuales o adquirirlos por separado.
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Image */}
            <div className="rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://images.unsplash.com/photo-1556742393-d75f468bfcb0?w=800&h=600&fit=crop" 
                alt="Restaurante Profesional" 
                className="w-full h-[300px] object-cover"
              />
            </div>

            {/* Features */}
            <Card className="border-2 border-[#212B35]">
              <CardContent className="pt-6">
                <h3 className="text-2xl font-bold text-[#212B35] mb-6">¿Qué incluye el Pack Sala?</h3>
                
                <div className="bg-blue-50 rounded-lg p-4 mb-6">
                  <h4 className="font-bold text-blue-900 mb-3 text-lg flex items-center gap-2">
                    <Check className="w-5 h-5 text-blue-600" />
                    Todo lo del Pack Barra:
                  </h4>
                  <ul className="space-y-2 text-sm text-gray-700 ml-7">
                    <li>• Software de punto de venta (TPV)</li>
                    <li>• Soporte 7 días/semana</li>
                    <li>• Carta por QR básica gratuita</li>
                    <li>• Decisions C10 - app móvil</li>
                    <li>• Administrador para editar carta</li>
                    <li>• Más de 60 informes</li>
                  </ul>
                </div>

                <div className="border-t-2 border-green-600 pt-4">
                  <h4 className="font-bold text-green-700 mb-4 text-lg">+ Ventajas adicionales:</h4>
                  <ul className="space-y-4">
                    {[
                      'Licencia para 5 comanderos adicionales incluida',
                      'Gestión de comandas desde sala con tablets',
                      'Mayor control de pedidos en tiempo real',
                      'Comunicación directa entre sala y cocina',
                      'Reducción de errores en pedidos',
                      'Servicio más rápido y eficiente'
                    ].map((feature, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <div className="bg-green-100 rounded-full p-1 mt-0.5">
                          <Check className="w-4 h-4 text-green-600" />
                        </div>
                        <span className="text-gray-700 font-medium">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </CardContent>
            </Card>

            {/* Installation Offer */}
            <Card className="bg-gradient-to-r from-orange-500 to-red-500 text-white border-0 shadow-xl">
              <CardContent className="pt-6">
                <div className="flex items-start gap-3 mb-4">
                  <Calendar className="w-8 h-8 flex-shrink-0" />
                  <div>
                    <h3 className="text-2xl font-bold mb-2">🎉 Oferta Especial de Instalación</h3>
                    <p className="text-white/90 text-sm mb-3">Válida si contratas en máximo 7 días</p>
                  </div>
                </div>
                
                <div className="bg-white/20 rounded-xl p-4 backdrop-blur-sm">
                  <div className="flex items-center justify-center gap-4 mb-2">
                    <span className="text-2xl line-through text-white/70">250€ + IVA</span>
                    <span className="text-xl">→</span>
                    <span className="text-4xl font-bold">125€ + IVA</span>
                  </div>
                  <p className="text-center text-sm font-bold">50% DE DESCUENTO</p>
                </div>
                
                <p className="text-xs text-white/80 mt-3 text-center">
                  Instalación completa y formación para tu equipo incluidas
                </p>
              </CardContent>
            </Card>

            {/* Benefits */}
            <div className="bg-gradient-to-r from-[#9C0F17] to-[#7A0C12] text-white rounded-2xl p-8 shadow-xl">
              <h3 className="text-2xl font-bold mb-4">¿Por qué elegir el Pack Sala?</h3>
              <ul className="space-y-3">
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Perfecto para restaurantes con sala y servicio de mesa</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>5 comanderos incluidos - gestiona hasta 5 camareros</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Sin permanencia - cancela cuando quieras</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Soporte 7 días/semana</span>
                </li>
                <li className="flex items-center gap-2">
                  <Check className="w-5 h-5" />
                  <span>Compatible con tus equipos actuales</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Right Column - Contact Form */}
          <div className="lg:sticky lg:top-8">
            <Card className="shadow-2xl border-2 border-[#9C0F17]">
              <CardContent className="pt-8">
                {!submitted ? (
                  <>
                    <div className="text-center mb-6">
                      <div className="bg-orange-100 text-orange-700 px-4 py-2 rounded-lg inline-flex items-center gap-2 mb-3">
                        <Calendar className="w-5 h-5" />
                        <span className="font-bold">Oferta instalación 50% válida 7 días</span>
                      </div>
                      <h2 className="text-3xl font-bold text-[#212B35] mb-2">
                        Solicita información sin compromiso
                      </h2>
                      <p className="text-gray-600">
                        Te llamamos para resolver todas tus dudas sobre el Pack Sala
                      </p>
                    </div>

                    <form onSubmit={handleSubmit} className="space-y-5">
                      <div>
                        <Label htmlFor="nombre" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#9C0F17]" />
                          Nombre completo *
                        </Label>
                        <Input
                          id="nombre"
                          name="nombre"
                          type="text"
                          required
                          value={formData.nombre}
                          onChange={handleChange}
                          placeholder="Tu nombre y apellidos"
                          className="h-12 text-base"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="email" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Mail className="w-4 h-4 text-[#9C0F17]" />
                          Email *
                        </Label>
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          required
                          value={formData.email}
                          onChange={handleChange}
                          placeholder="tu@email.com"
                          className="h-12 text-base"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="telefono" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Phone className="w-4 h-4 text-[#9C0F17]" />
                          Teléfono *
                        </Label>
                        <Input
                          id="telefono"
                          name="telefono"
                          type="tel"
                          required
                          value={formData.telefono}
                          onChange={handleChange}
                          placeholder="600 123 456"
                          className="h-12 text-base"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="empresa" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <Building2 className="w-4 h-4 text-[#9C0F17]" />
                          Nombre del restaurante (opcional)
                        </Label>
                        <Input
                          id="empresa"
                          name="empresa"
                          type="text"
                          value={formData.empresa}
                          onChange={handleChange}
                          placeholder="Nombre de tu restaurante"
                          className="h-12 text-base"
                          disabled={loading}
                        />
                      </div>

                      <div>
                        <Label htmlFor="necesita_hardware" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <AlertCircle className="w-4 h-4 text-[#9C0F17]" />
                          ¿Necesitas hardware (TPV, impresoras, tablets, comanderos)? *
                        </Label>
                        <select
                          id="necesita_hardware"
                          name="necesita_hardware"
                          required
                          value={formData.necesita_hardware}
                          onChange={handleChange}
                          className="w-full h-12 text-base border border-gray-300 rounded-md px-3 focus:outline-none focus:ring-2 focus:ring-[#9C0F17]"
                          disabled={loading}
                        >
                          <option value="">Selecciona una opción</option>
                          <option value="Si, necesito equipos nuevos (TPV + 5 comanderos)">Sí, necesito equipos nuevos (TPV + 5 comanderos)</option>
                          <option value="No, tengo mis propios equipos">No, tengo mis propios equipos</option>
                          <option value="Tengo algunos equipos, necesito completar">Tengo algunos equipos, necesito completar</option>
                          <option value="No estoy seguro, necesito asesoramiento">No estoy seguro, necesito asesoramiento</option>
                        </select>
                      </div>

                      <div>
                        <Label htmlFor="mensaje" className="text-base font-semibold text-gray-700 mb-2 flex items-center gap-2">
                          <MessageSquare className="w-4 h-4 text-[#9C0F17]" />
                          Mensaje o consulta (opcional)
                        </Label>
                        <Textarea
                          id="mensaje"
                          name="mensaje"
                          value={formData.mensaje}
                          onChange={handleChange}
                          placeholder="Cuéntanos sobre tu restaurante o cualquier consulta adicional..."
                          rows={3}
                          className="text-base resize-none"
                          disabled={loading}
                        />
                      </div>

                      <Button
                        type="submit"
                        disabled={loading}
                        className="w-full h-14 text-lg font-bold bg-[#9C0F17] hover:bg-[#7A0C12] text-white"
                      >
                        {loading ? (
                          <>
                            <Loader2 className="w-5 h-5 mr-2 animate-spin" />
                            Enviando...
                          </>
                        ) : (
                          '¡Quiero más información!'
                        )}
                      </Button>

                      <p className="text-xs text-gray-500 text-center">
                        Al enviar este formulario aceptas nuestra política de privacidad.
                        Te contactaremos para resolver tus dudas sobre el Pack Sala.
                      </p>
                    </form>
                  </>
                ) : (
                  <div className="text-center py-8">
                    <div className="bg-green-100 rounded-full w-20 h-20 flex items-center justify-center mx-auto mb-4">
                      <Check className="w-10 h-10 text-green-600" />
                    </div>
                    <h3 className="text-2xl font-bold text-[#212B35] mb-2">
                      ¡Solicitud enviada!
                    </h3>
                    <p className="text-gray-600 mb-6">
                      Hemos recibido tu solicitud. Un miembro de nuestro equipo se pondrá en contacto contigo en las próximas 24 horas.
                    </p>
                    <Button
                      onClick={() => setSubmitted(false)}
                      variant="outline"
                      className="border-[#9C0F17] text-[#9C0F17] hover:bg-[#9C0F17] hover:text-white"
                    >
                      Enviar otra solicitud
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Trust Badges */}
            <div className="mt-6 text-center">
              <p className="text-sm text-gray-600 mb-2">Más de 1000 establecimientos confían en nosotros</p>
              <div className="flex justify-center items-center gap-4 text-xs text-gray-500">
                <span>✓ 5 comanderos incluidos</span>
                <span>✓ Soporte 7 días/semana</span>
                <span>✓ Sin permanencia</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#212B35] text-white py-8 mt-20">
        <div className="max-w-7xl mx-auto px-4 text-center">
          <p className="text-sm text-white/70">
            © 2025 Camarero10. Todos los derechos reservados.
          </p>
        </div>
      </footer>
    </div>
  );
}
